import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import '@angular/compiler';
import { ErrorHandler, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { bootstrapApplication, DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, ROUTES } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthHttpInterceptor, AuthModule, AuthService } from '@auth0/auth0-angular';
import { provideQueryDevTools } from '@ngneat/query-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppComponent } from 'app/app.component';
import { ApiInterceptor } from 'app/core/interceptors/api.interceptor';
import { ExceptionInterceptor } from 'app/core/interceptors/exception-interceptor';
import { ContextService } from 'app/core/services/context.service';
import { ErrorHandlerService } from 'app/core/services/error-handler.service';
import { UsersService } from 'app/features/user-management/services/users.service';
import { environment } from 'environment/environment';
import { LaunchDarklyService } from 'shared/services/launch-darkly-service';
import { rootRoutes, routeFactory } from 'app/app.routes';

if (environment.production) {
  const script = document.createElement('script');
  script.src = 'https://cdn.whatfix.com/prod/cf450f2b-5674-4a95-b67b-000779a7f771/initiator/initiator.nocache.js';
  document.head.appendChild(script);
}

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeIcons(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
  return () => {
    matIconRegistry.addSvgIcon(
      'custom-depart-trailer',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/custom-icons/depart-trailer.svg'),
    );
    matIconRegistry.addSvgIcon(
      'custom-arrive-trailer',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/custom-icons/arrive-trailer.svg'),
    );
    matIconRegistry.addSvgIcon(
      'custom-reactivate',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/custom-icons/reactivate.svg'),
    );
    matIconRegistry.addSvgIcon(
      'custom-substitute-trailer',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/custom-icons/substitute-trailer.svg'),
    );
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ExceptionInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    provideNativeDateAdapter(),
    provideAppInitializer(() => initializeIcons(inject(MatIconRegistry), inject(DomSanitizer))()),
    importProvidersFrom(
      AuthModule.forRoot({
        domain: environment.auth0Domain,
        clientId: environment.auth0ClientId,
        authorizationParams: {
          redirect_uri: window.location.origin,
          connection: environment.auth0Connection,
          audience: environment.auth0Audience,
        },
        useRefreshTokens: true,
        httpInterceptor: {
          allowedList: [
            `${environment.authApiBaseUrl}*`,
            `${environment.siteManagementApiBaseUrl}*`,
            `${environment.taskManagementApiBaseUrl}*`, // TODO: firefly-1-9758-ui remove after merge flag expires
            `${environment.trailersApiBaseUrl}*`, // TODO: firefly-1-9758-ui remove after merge flag expires
            `${environment.yardOpsApiBaseUrl}*`,
          ],
        },
      }),
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
      AngularSvgIconModule.forRoot(),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
    ),
    provideAppInitializer(() => {
      const initializerFn = ((userService: UsersService, authService: AuthService) => () => {
        authService.isAuthenticated$.subscribe((isAuthenticated) => {
          if (isAuthenticated) {
            userService.getLoggedInUserWithRoles().subscribe();
          }
        });
      })(inject(UsersService), inject(AuthService));
      return initializerFn();
    }),
    {
      provide: ROUTES,
      useFactory: (contextService: ContextService) => routeFactory(contextService),
      deps: [ContextService, LaunchDarklyService],
      multi: true,
    },
    provideRouter(rootRoutes),
    environment.production ? [] : provideQueryDevTools(),
  ],
}).catch((err) => console.error(err));
